import { useDisclosure } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { PageLayout } from '@/components/templates/PageLayout';
import { CmLongLoading } from '@/components/uiParts';
import { isAvailableFeature } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { CanNotUpdateMenuManagementNotice } from '@/pages/menu-managements/components/Alert/CanNotUpdateMenuManagementNotice';
import { MenuManagementEditButtonArea } from '@/pages/menu-managements/components/TopButtonArea';
import { MenuManagementItemsForm } from '@/pages/menu-managements/items/components/Form';
import { useMenuManagementItemForm } from '@/pages/menu-managements/items/hooks';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementDeleteBrandItem } from '@/services/BrandItem/hooks/delete/useMenuManagementDeleteBrandItem';

const MenuManagementItemsEditPage: React.FC = (): JSX.Element => {
  const { id: urlParamsId } = useParams<{ id: string }>();
  const history = useHistory();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    associateBrandSelectInput,
    titleJa,
    image,
    memo,
    description,
    salesStartAt,
    salesEndAt,
    canServeAlone,
    orderMethods,
    error,
    searchItems,
    selectedItems,
    searchItemName,
    isSearching,
    takeout,
    delivery,
    eatIn,
    initialStringifyFormState,
    isLoading,
    limitOfPurchase,
    handleChangeLimitOfPurchase,
    setIsLoading,
    handleInputName,
    handleChangeImageUrl,
    handleClearImage,
    handleChangeCanServeAlone,
    handleChangeOrderMethod,
    handleInputMemo,
    handleInputDescription,
    handleInputSalesStartAt,
    handleInputSalesEndAt,
    handleInputTakeoutPrice,
    handleInputTakeoutPosCode,
    handleInputTakeoutPosCodeCaseOptionGroupItem,
    handleInputTakeoutTax,
    handleInputDeliveryPrice,
    handleInputDeliveryPosCode,
    handleInputDeliveryPosCodeCaseOptionGroupItem,
    handleInputDeliveryTax,
    handleInputEatInPrice,
    handleInputEatInPosCode,
    handleInputEatInPosCodeCaseOptionGroupItem,
    handleInputEatInTax,
    handleOnDragEnd,
    handleSearchItemNameInput,
    handleAddSelectItems,
    handleDeleteDragItem,
    handleUpdate,
    handleDuplicate,
    initializeEditPageState,
    isAuthRequired,
    handleChangeIsAuthRequired,
    salesPeriodType,
    handleSalesPeriodType,
    handleSalesRelativeStartAt,
    handleSalesRelativeEndAt,
    salesRelativeStartAt,
    salesRelativeEndAt,
    specialInstructionsEnabled,
    specialInstructionsTitle,
    specialInstructionsPlaceholder,
    specialInstructionsRequired,
    handleChangeSpecialInstructionsEnabled,
    handleInputSpecialInstructionsTitle,
    handleInputSpecialInstructionsPlaceholder,
    handleChangeSpecialInstructionsRequired,
  } = useMenuManagementItemForm();

  const isEdit =
    JSON.stringify([
      titleJa,
      memo,
      limitOfPurchase,
      description,
      salesStartAt,
      salesEndAt,
      image,
      selectedItems,
      takeout,
      delivery,
      orderMethods,
      isAuthRequired,
      salesPeriodType,
      salesRelativeStartAt,
      salesRelativeEndAt,
    ]) !== initialStringifyFormState; // WARNING 文字列比較なので順番を変更するときは注意

  const isDisabledSubmitButton = (() => {
    const hasTakeout = orderMethods.includes('TAKE_OUT');
    const hasDelivery = orderMethods.includes('DELIVERY');
    return [
      error.titleJa.isError,
      error.description.isError,
      error.salesPeriod.isError,
      error.relativeSalesPeriod.isError,
      error.memo.isError,
      orderMethods.length === 0,
      hasTakeout && takeout.price === '',
      hasTakeout && error.takeoutPrice.isError,
      hasDelivery && delivery.price === '',
      hasDelivery && error.deliveryPrice.isError,
      !isEdit,
      // 商品に関する要望欄が有効でタイトルが空の場合はエラー
      specialInstructionsEnabled && specialInstructionsTitle === '',
      error.specialInstructionsTitle?.isError,
      error.specialInstructionsPlaceholder?.isError,
    ].some((error) => error);
  })();

  const listUrl = routes.menuManagementItems.path;

  const handleDelete = () => {
    (async () => {
      try {
        /* バックエンドでmongoのトランザクションを実装しているがローカルだと500エラになる。 */
        setIsLoading(true);
        await useMenuManagementDeleteBrandItem(urlParamsId);
        setIsLoading(false);
        alert('商品を削除しました。');
        history.push(listUrl);
      } catch (error) {
        setIsLoading(false);
        if (error instanceof HttpError && error.status === 400) {
          return alert(error.message);
        }
        // @ts-ignore TS2322
        errorHandler({ error });
      }
    })();
  };

  useEffect(() => {
    (async () => {
      await initializeEditPageState(urlParamsId);
    })();
  }, []);

  // 店内飲食のアップセル対応
  const { managerAccountMe } = useManagerAccountMe();
  const isAvailableEatin = isAvailableFeature(
    'camel_order_eatin',
    managerAccountMe
  );

  return (
    <PageLayout
      title={routes.menuManagementItemsEdit.title}
      associateBrandSelectInput={{
        ...associateBrandSelectInput,
        isDisabled: true,
      }}
      alert={<CanNotUpdateMenuManagementNotice pageName={'商品'} />}
      disableScroll
    >
      <MenuManagementEditButtonArea
        name="商品"
        isOpenDeleteOpen={isOpen}
        handleOpenDeleteModal={onOpen}
        handleCloseDeleteModal={onClose}
        {...{
          listUrl,
          urlParamsId,
          isDisabledSubmitButton,
          handleUpdate,
          handleDuplicate,
          handleDelete,
        }}
      />
      <MenuManagementItemsForm
        brandSelectValue={associateBrandSelectInput.value?.value ?? ''}
        page="edit"
        {...{
          titleJa,
          image,
          memo,
          description,
          salesStartAt,
          salesEndAt,
          isSearching,
          searchItems,
          selectedItems,
          searchItemName,
          error,
          takeout,
          delivery,
          eatIn,
          canServeAlone,
          limitOfPurchase,
          handleChangeLimitOfPurchase,
          orderMethods,
          isAvailableEatin,
          handleInputName,
          handleChangeImageUrl,
          handleClearImage,
          handleInputMemo,
          handleInputDescription,
          handleInputSalesStartAt,
          handleInputSalesEndAt,
          handleInputTakeoutPrice,
          handleInputTakeoutPosCode,
          handleInputTakeoutPosCodeCaseOptionGroupItem,
          handleInputTakeoutTax,
          handleInputDeliveryPrice,
          handleInputDeliveryPosCode,
          handleInputDeliveryPosCodeCaseOptionGroupItem,
          handleInputDeliveryTax,
          handleInputEatInPrice,
          handleInputEatInPosCode,
          handleInputEatInPosCodeCaseOptionGroupItem,
          handleInputEatInTax,
          handleOnDragEnd,
          handleSearchItemNameInput,
          handleAddSelectItems,
          handleDeleteDragItem,
          handleChangeCanServeAlone,
          handleChangeOrderMethod,
          isAuthRequired,
          handleChangeIsAuthRequired,
          salesPeriodType,
          handleSalesPeriodType,
          handleSalesRelativeStartAt,
          handleSalesRelativeEndAt,
          salesRelativeStartAt,
          salesRelativeEndAt,
          specialInstructionsEnabled,
          specialInstructionsTitle,
          specialInstructionsPlaceholder,
          specialInstructionsRequired,
          handleChangeSpecialInstructionsEnabled,
          handleInputSpecialInstructionsTitle,
          handleInputSpecialInstructionsPlaceholder,
          handleChangeSpecialInstructionsRequired,
        }}
      />
      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default MenuManagementItemsEditPage;
